import { useEffect } from 'react';
import { Ico, autoImgs } from '../shared';
import { PageHeader, SectionCTA } from '../Layout';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About Us | Ratnam Techo Inc. – Precision Engineering Ontario';
  }, []);

  const values = [
    { icon: 'target',  title: 'Precision First',    desc: 'Every dimension matters. We hold tight tolerances because your project demands it.' },
    { icon: 'gem',     title: 'Uncompromising Quality', desc: 'From raw material to finished product, quality is never negotiated.' },
    { icon: 'shield',  title: 'Client Partnership',  desc: 'We treat every project as if it were our own — with full accountability.' },
    { icon: 'clock',   title: 'On-Time Delivery',    desc: 'We respect your deadlines and keep you informed at every stage.' },
    { icon: 'layers',  title: 'Innovation Driven',   desc: 'Constantly evolving our techniques to deliver better, faster, smarter results.' },
    { icon: 'wrench',  title: 'Hands-On Expertise',  desc: 'Decades of real shop-floor experience behind every project we take on.' },
  ];

  const capabilities = [
    'Wire EDM precision cutting',
    'CNC machining & turning',
    'MIG/TIG welding & fabrication',
    'Glass burner / torch manufacturing',
    'Custom prototype development',
    'Equipment repair & overhaul',
    'Automotive die manufacturing',
    'Mobile on-site service',
  ];

  return (
    <>
      <PageHeader
        title="About"
        highlight="Ratnam Techo Inc."
        sub="10+ years of precision engineering, custom fabrication, and glass burner manufacturing in Ontario, Canada."
        crumb="About Us"
      />

      {/* Our Story */}
      <section className="section" style={{ background: 'var(--white)' }}>
        <div className="section-inner">
          <div className="about-grid">
            <div className="about-visual">
              <div className="about-img-wrap">
                <img src={autoImgs[0]} alt="Our workshop" className="about-photo-main" />
                <div className="about-photo-row">
                  <img src={autoImgs[1]} alt="Precision machining" className="about-photo-thumb" />
                  <img src={autoImgs[2]} alt="Custom fabrication" className="about-photo-thumb" />
                </div>
              </div>
              <div className="about-badge">
                <div className="about-badge-num">10+</div>
                <div className="about-badge-text">Years of<br />Excellence</div>
              </div>
            </div>

            <div>
              <div className="section-tag">
                <span className="section-tag-line" />
                Our Story
              </div>
              <h2 className="section-title">
                Built on Craft,<br />
                <span className="blue">Driven by Precision</span>
              </h2>
              <p className="section-sub" style={{ marginBottom: 16 }}>
                Ratnam Techo Inc. was founded in Ontario, Canada with a singular mission: to deliver
                precision-engineered products that professionals can rely on day after day. What began
                as a small fabrication operation has grown into a full-service engineering facility
                trusted by artists, manufacturers, and innovators alike.
              </p>
              <p className="section-sub">
                Over more than a decade, we have refined our processes, invested in advanced
                equipment, and cultivated deep expertise in glass burner manufacturing, Wire EDM
                cutting, CNC machining, and custom product development. Every project we take on
                carries the full weight of our commitment to quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section" style={{ background: 'var(--off-white)' }}>
        <div className="section-inner">
          <div className="two-col-section">
            <div>
              <div className="section-tag">
                <span className="section-tag-line" />
                What We Do
              </div>
              <h2 className="section-title">
                Full-Spectrum<br />
                <span className="gold">Capabilities</span>
              </h2>
              <p className="section-sub" style={{ marginBottom: 36 }}>
                From the first sketch to the finished part, our facility is equipped to handle
                every phase of your project with speed and precision.
              </p>
              <ul className="capability-list">
                {capabilities.map((c, i) => (
                  <li key={i}>
                    <span className="capability-check"><Ico name="check" size={14} /></span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="about-imgs-stacked">
              <img src={autoImgs[3]} alt="Automotive die work" className="stacked-img stacked-main" />
              <div className="stacked-pair">
                <img src={autoImgs[4]} alt="Precision part" className="stacked-img" />
                <img src={autoImgs[5]} alt="Finished fabrication" className="stacked-img" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section why" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div className="why-bg" />
        <div className="section-inner" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center' }}>
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <span className="section-tag-line" />
              Our Core Values
            </div>
            <h2 className="section-title" style={{ color: 'white', textAlign: 'center' }}>
              What We Stand <span className="gold">For</span>
            </h2>
          </div>
          <div className="why-grid" style={{ marginTop: 40 }}>
            {values.map((v, i) => (
              <div className="why-card" key={i}>
                <div className="why-icon"><Ico name={v.icon} size={22} /></div>
                <h3 className="why-title">{v.title}</h3>
                <p className="why-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionCTA heading="Ready to Work With Us?" sub="Get in touch today and let's discuss how we can bring your project to life." />
    </>
  );
}
